import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Fade,
  Backdrop,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Link,
} from '@mui/material';
import { DateTimeInput } from '../../../../shared/ReusableComponents/DateTimeInput';
import { Button, Input } from '../../../../shared/ReusableComponents';
import { DateFormatter, DateTimeFormats } from '../../../../shared/utils/DateFormatter';
import { useSelector, useDispatch } from 'react-redux';
import { CommonFunctions } from '../../../../shared/utils/CommonFunctions';
import moment from 'moment';
import restApiService from '../../../../services/restApiService';
import { toast } from '../../../../store/toastSlice';
import { AlertType, OnceOnlyMedicine } from '../../../../shared/constants/enums';
import { hideLoader, showLoader } from '../../../../store/currentPreferencesSlice';
import { apiEndpoints } from '../../../../shared/constants/api-constant';
import useGet from '../../../../services/hooks/useGet';
import { IReason } from '../PrescribedMedication/types';
import { cssVar } from '../../../../theme/theme';
import CloseBtn from '../../../../shared/ReusableComponents/CloseBtn';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '50%',
  maxWidth: '50%',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 1,
  px: '4.375rem',
  py: '1.875rem',
  backgroundColor: '#F5F5F5',
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    margin: '0px 0px',
    padding: '0px 0px',
    '&.Mui-focused fieldset': {
      borderColor: '#000000',
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: '#000000',
      borderWidth: '1px',
    },
    '&.Mui-disabled': {
      backgroundColor: '#F5F5F5',
      '& fieldset': {
        borderColor: '#B1B1B1',
      },
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#000000',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#000000',
    borderWidth: '1px',
  },
  '& .MuiTableCell-root': {
    padding: '10px',
  },
};

interface IOneTimeMedicationModalProps {
  open: boolean;
  onClose: () => void;
  medication: any;
  onSubmit: () => void;
}

const OneTimeMedicationModal: React.FC<IOneTimeMedicationModalProps> = ({
  open,
  onClose,
  medication,
  onSubmit,
}) => {
  const [showInputs, setShowInputs] = useState(true);
  const [dose, setDose] = useState('');
  const [doseError, setDoseError] = useState(false);
  const [counterSignedBy, setCounterSignedBy] = useState('');
  const [counterSignedByError, setCounterSignedByError] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);
  const [reasons, setReasons] = useState<IReason[] | null>(null);
  const [vteAssessed, setVteAssessed] = useState(false);
  const [prophylaxisNotRequired, setProphylaxisNotRequired] = useState(medication?.prophylaxis ?? false);
  const [contraindicated, setContraindicated] = useState(medication?.contraindicated ?? false);
  const [administerTime, setAdministerTime] = useState(moment());




  const auth = useSelector((state: any) => state.login);
  const signedBy = CommonFunctions.formatFirstAndLastName(
    auth.userInfo?.userFirstName,
    auth.userInfo?.userLastName,
    { suffixKey: 'sn' }
  );
  const dispatch = useDispatch();
  const { data, getService } = useGet();

  useEffect(() => {
    if (!data && !showInputs) {
      getService(`${apiEndpoints.REASONS}`);
    }
  }, [showInputs, getService, data]);

  useEffect(() => {
    if (data) {
      setReasons(data);
    } else {
      setReasons(null);
    }
  }, [data]);

  const tableHeaders = [
    { label: 'Medicine', key: 'medicineName' },
    { label: 'Route', key: 'route' },
    { label: 'Dose', key: 'dose' },
    { label: 'Indication', key: 'indication' },
  ];

  const handleDoseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDose(e.target.value);
    if (e.target.value.trim()) setDoseError(false);
  };

  const handleCounterSignedByChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCounterSignedBy(e.target.value);
    if (e.target.value.trim()) setCounterSignedByError(false);
  };

  const handleReasonChange = (e: any) => {
    setReason(e.target.value);
    if (e.target.value) setReasonError(false);
  };

  const handleNotGivenClick = () => {
    setShowInputs(false);
    setCounterSignedBy('');
    setCounterSignedByError(false);
  };

  const resetFields = () => {
    setShowInputs(true);
    setDose('');
    setDoseError(false);
    setCounterSignedBy('');
    setCounterSignedByError(false);
    setReason('');
    setReasons(null);
    setVteAssessed(false);
    setProphylaxisNotRequired(medication?.prophylaxis ?? false);
    setContraindicated(medication?.contraindicated ?? false);
    setAdministerTime(moment());
    onClose();
  };

  const handleSubmit = async () => {
    let hasError = false;
    if (showInputs) {
      if (!dose.trim()) {
        setDoseError(true);
        hasError = true;
      }
      if (!counterSignedBy.trim()) {
        setCounterSignedByError(true);
        hasError = true;
      }
    } else {
      if (!reason) {
        setReasonError(true);
        hasError = true;
      }
      if (!counterSignedBy.trim()) {
        setCounterSignedByError(true);
        hasError = true;
      }
    }
    if (hasError) return;

    dispatch(showLoader());
    try {
      const payload = {
        dose: dose.trim(),
        administerOn: administerTime.toISOString(),
        isGiven: showInputs,
        reason: reason,
        scheduleTimeId: medication.frequency[0].doseDetails.id,
        prescribedId: medication.frequency[0].doseDetails.prescribedId,
        scheduleTime: administerTime.format("HH:mm"),
        signedBy: signedBy,
        counterSignedBy: counterSignedBy.trim(),
        updatedOn: new Date().toISOString(),
        ...(showInputs && {
          vteAssessed,
          prophylaxis: prophylaxisNotRequired,
          contraindicated,
        }),
      };
      await restApiService.post(`${apiEndpoints.MEDICATIONS}`, payload);
      dispatch(
        toast({
          isOpen: true,
          message: showInputs
            ? `${medication.medicineName} ${OnceOnlyMedicine} has been Successfully Administered.`
            : `${medication.medicineName} ${OnceOnlyMedicine} has not been Administered.`,
          type: showInputs ? AlertType.SUCCESS : AlertType.WARNING,
        })
      );
      onSubmit();
      resetFields();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'An error occurred.';
      dispatch(toast({ isOpen: true, message: errorMessage, type: AlertType.ERROR }));
    } finally {
      dispatch(hideLoader());
    }
  };

  return (
    <Modal
      open={open}
      onClose={resetFields}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <Fade in={open}>
        <Box sx={style}>
          <CloseBtn onClose={resetFields} />
          <Box sx={{ textAlign: 'center' }}>
            <span className="material-symbols-outlined medicine-icon" style={{ fontSize: '50px' }}>
              vaccines
            </span>
          </Box>

          <div style={{ overflowX: 'auto', marginTop: 2 }}>
            <Table sx={{ mt: 2 }}>
              <TableHead
                sx={{
                  backgroundColor: 'var(--color-default)',
                  border: '2px solid #F5F5F5',
                }}
              >
                <TableRow>
                  {tableHeaders.map((header) => (
                    <TableCell key={header.key}>
                      <strong>{header.label}</strong>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow
                  sx={{
                    backgroundColor: '#fff',
                  }}
                  className="tabledata"
                >
                  <TableCell>{medication?.medicineName}</TableCell>
                  <TableCell>{medication?.route}</TableCell>
                  <TableCell>{medication?.dose}</TableCell>
                  <TableCell>{medication?.indication}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {showInputs && (
            <Box
              sx={{
                marginLeft: '-10px',
                marginRight: '-10px',
                width: '100%',
                flexWrap: 'nowrap',
                overflowX: 'auto',
                mt: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    variant="body1"
                    sx={{ marginRight: '20px', whiteSpace: 'nowrap' }}
                  >
                    VTE risk assessed:
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={vteAssessed}
                        onChange={(e) => setVteAssessed(e.target.checked)}
                      />
                    }
                    label={<Typography variant="body1">Yes</Typography>}
                  />
                </div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={prophylaxisNotRequired}
                      onChange={(e) => setProphylaxisNotRequired(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body1" sx={{ whiteSpace: 'nowrap' }}>
                      Prophylaxis not required
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={contraindicated}
                      onChange={(e) => setContraindicated(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body1">Contraindicated</Typography>}
                />
              </Box>
            </Box>
          )}

          {showInputs && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                   <Input
                    label="Date"
                    fullWidth
                    value={DateFormatter.formatDate(administerTime.toISOString())}
                    disabled
                  /> 
                   {/* <DateTimeInput
                    label="Date"
                    name="date"
                    type="date"
                    value={DateFormatter.formatDate(administerTime.toISOString())}
                    onChange={setAdministerTime}
                    format={DateTimeFormats.DateFormat}
                    size="small"
                    isDisabled={true} */}
                    
                  
                </Grid>
                <Grid item xs={3}>
                  <DateTimeInput
                    label="Time"
                    name="time"
                    type="time"
                    value={administerTime}
                    onChange={setAdministerTime}
                    format={DateTimeFormats.TimeFormat}
                    size="small"
                    
                  />
                </Grid>
                <Grid item xs={6}>
                  <Input
                    label="Dose"
                    fullWidth
                    required
                    value={dose}
                    onChange={handleDoseChange}
                    error={doseError}
                    hasError={doseError ? 'Dose is required' : ''}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {showInputs && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Input
                    label="Signed by (SN)"
                    fullWidth
                    value={signedBy}
                    disabled
                  />
                </Grid>
                <Grid item xs={6}>
                  <Input
                    label="Counter Signed By (RN)"
                    fullWidth
                    required
                    value={counterSignedBy}
                    onChange={handleCounterSignedByChange}
                    error={counterSignedByError}
                    hasError={counterSignedByError ? 'Counter Signed By (RN) is required' : ''}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {!showInputs && (
            <Box sx={{ mt: 2 }}>
              <Grid container justifyContent="center" alignItems="center" spacing={2}>
                <Grid item>
                  <FormControl fullWidth error={reasonError}>
                    <Typography variant="body1" sx={{ marginRight: '10px' }}>
                      Reasons *:
                    </Typography>
                    <Select
                      value={reason}
                      onChange={handleReasonChange}
                      size="small"
                      displayEmpty
                      sx={{
                        color: reasonError ? 'var(--color-error-main)' : 'primary.main',
                        width: '391px',
                        height: '40px',
                        marginLeft: '10px',
                        border: reasonError ? '1px solid var(--color-error-main)' : 'none',
                        backgroundColor: '#FFFFFF',
                        borderColor: 'transparent',
                        '& fieldset': {
                          border: reasonError ? '1px solid var(--color-error-main)' : 'none',
                        },
                        '&:hover fieldset': {
                          border: reasonError ? '1px solid var(--color-error-main)' : 'none',
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            overflowY: 'auto',
                          },
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        Select
                      </MenuItem>
                      {reasons &&
                        Array.isArray(reasons) &&
                        reasons.map((item: IReason) => (
                          <MenuItem value={item.reason} key={item.id}>
                            {item.reason}
                          </MenuItem>
                        ))}
                    </Select>
                    {reasonError && (
                      <FormHelperText error sx={{ ml: '17%', fontSize: '10px' }}>
                        Reason is Required
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}

          {reason && (
            <Box sx={{ mt: 2 }}>
              <Grid container justifyContent="center" alignItems="center">
                <Grid item xs={4}>
                  <Input
                    label="Counter Signed By (RN)"
                    required
                    value={counterSignedBy}
                    onChange={handleCounterSignedByChange}
                    error={counterSignedByError}
                    hasError={counterSignedByError ? 'Counter Signed By (RN) is required' : ''}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          <Box
            sx={{
              mt: 4,
              display: 'flex',
              justifyContent: 'center',
              columnGap: '20px',
              alignItems: 'center',
            }}
          >
            {/* <Link
              sx={{
                cursor: 'pointer',
                color: 'info.main',
                textDecorationColor: cssVar('--color-info-main'),
                flexShrink: 0,
              }}
              onClick={handleNotGivenClick}
            >
              <span>Not Administered</span>
            </Link> */}
            <Button
              text="Cancel"
              variant="outlined"
              onClick={resetFields}
              fullWidth={false}
            />
            <Button
              text="Administer"
              variant="contained"
              onClick={handleSubmit}
              fullWidth={false}
            />
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default OneTimeMedicationModal;
